import React, { Component } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

function formatProductLabel(product) {
  if (!product) return "";
  const parts = [];
  if (product.brand) parts.push(product.brand);
  if (product.displayName && product.displayName !== product.brand) parts.push(product.displayName);
  if (product.displaySize) parts.push(product.displaySize);
  if (product.ean) parts.push(`EAN: ${product.ean}`);
  return parts.join(" · ");
}

class OnDemandProductSearchField extends Component {
  state = {
    options: [],
    loading: false,
    inputValue: "",
  };

  searchTimer = null;

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  handleInputChange = (event, value, reason) => {
    this.setState({ inputValue: value || "" });
    if (reason === "reset") return;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const query = String(value || "").trim();
    if (query.length < 2) {
      this.setState({ options: [], loading: false });
      return;
    }

    this.setState({ loading: true });
    this.searchTimer = setTimeout(() => {
      fetch(
        `${basePath}/api/v1/on-demand-orders/products/search?q=${encodeURIComponent(query)}`,
        { headers: utils.getAdminAuthHeaders() }
      )
        .then((response) => response.json())
        .then((data) => {
          this.setState({
            options: data.ok ? data.products || [] : [],
            loading: false,
          });
        })
        .catch(() => {
          this.setState({ options: [], loading: false });
        });
    }, 280);
  };

  render() {
    const { label, value, onChange, disabled } = this.props;
    const { options, loading, inputValue } = this.state;

    return (
      <Autocomplete
        options={options}
        value={value || null}
        inputValue={inputValue}
        disabled={disabled}
        loading={loading}
        onChange={(event, product) => onChange(product)}
        onInputChange={this.handleInputChange}
        getOptionLabel={formatProductLabel}
        getOptionSelected={(option, selected) => option?.ean === selected?.ean}
        noOptionsText={inputValue.trim().length < 2 ? "הקלד לחיפוש מוצר" : "לא נמצאו מוצרים"}
        loadingText="מחפש..."
        renderOption={(option) => (
          <Box style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
            {option.imageUrl ? (
              <img
                src={option.imageUrl}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  objectFit: "contain",
                  border: "1px solid #e5e7eb",
                  borderRadius: 4,
                  background: "#f9fafb",
                }}
              />
            ) : (
              <Box
                style={{
                  width: 44,
                  height: 44,
                  border: "1px solid #e5e7eb",
                  borderRadius: 4,
                  background: "#f3f4f6",
                }}
              />
            )}
            <Box style={{ minWidth: 0 }}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {[option.brand, option.displayName].filter(Boolean).join(" ")}
              </Typography>
              <Typography variant="caption" style={{ color: "#6b7280", display: "block" }}>
                {option.displaySize || ""}
                {option.set ? " · סט" : ""}
                {option.ean ? ` · ${option.ean}` : ""}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  }
}

OnDemandProductSearchField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default OnDemandProductSearchField;
